import { weightedRandom } from 'src/replicant/utils/random';
import type { Match3 } from '.';
import { duration } from '../../utils/duration';

export type BoardPosition = { rowIndex: number; colIndex: number };
export type PositionSwap = { a: BoardPosition; b: BoardPosition };

export const puzzleDuration = duration({ seconds: 30 });

export const minMatches = 3;

export const rows = 5;

export const columns = 6;

export const replenishmentChance = {
  d: 0.19,
  f: 0.19,
  t: 0.19,
  p: 0.19,
  c: 0.19,
  g: 0.05,
};

export const rewardMilestones = [
  { points: 0, coins: 100 },
  { points: 1000, coins: 200 },
  { points: 3000, coins: 300 },
  { points: 8000, coins: 400 },
  { points: 15000, coins: 500 },
];

export function getRewardAmounts(points: number) {
  const rewards = { coins: 0 };
  for (let milestone of rewardMilestones) {
    if (points < milestone.points) {
      break;
    }
    rewards.coins = milestone.coins;
  }
  return rewards;
}

export const getInitialBoardState = (
  random: () => number,
): Match3.BoardState => {
  const board: Match3.BoardState = [];

  const weights = Object.values(replenishmentChance);
  const blockTypes = Object.keys(replenishmentChance) as Match3.BlockType[];

  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < columns; x++) {
      let blockType: Match3.BlockType = 'd';
      do {
        blockType = blockTypes[weightedRandom(weights, random)];
      } while (
        (x >= 2 &&
          board[x - 1][y] === blockType &&
          board[x - 2][y] === blockType) ||
        (y >= 2 &&
          board[x][y - 1] === blockType &&
          board[x][y - 2] === blockType)
      );

      if (!board[x]) {
        board[x] = [];
      }
      board[x][y] = blockType;
    }
  }

  return board;
};

type MatchReward = {
  type: 'points' | 'gems';
  base: number;
  multiplier: (matches: number) => number;
};

const pointReward: MatchReward = {
  type: 'points',
  base: 100,
  multiplier: (matches) => 1 + (matches - 3) / 3,
};
const gemReward: MatchReward = {
  type: 'gems',
  base: 1,
  multiplier: (matches) => matches,
};

export const rewards: Record<Match3.BlockType, MatchReward> = {
  d: pointReward,
  f: pointReward,
  t: pointReward,
  p: pointReward,
  c: pointReward,
  g: gemReward,
};

export const positionsEqual = (a: BoardPosition, b: BoardPosition) =>
  a?.rowIndex === b?.rowIndex && a?.colIndex === b?.colIndex;

export const positionToString = (pos: BoardPosition) =>
  `${pos.colIndex},${pos.rowIndex}`;

export const stringToPosition = (pos: string) => {
  const [colIndex, rowIndex] = pos.split(',');
  return { colIndex: +colIndex, rowIndex: +rowIndex };
};

export const isPositionOnBoard = ({ rowIndex, colIndex }: BoardPosition) =>
  rowIndex >= 0 && rowIndex < rows && colIndex >= 0 && colIndex < columns;

// Adapted from flood-fill algorithm by setting found elements to null
export const floodFind = (
  board: Match3.BoardState,
  position: BoardPosition,
  type: Match3.BlockType,
) => {
  const { colIndex, rowIndex } = position;
  const block = board[colIndex]?.[rowIndex];
  if (!type || !block || block !== type) {
    return [];
  }

  board[colIndex][rowIndex] = null; // Remove match from board
  return [
    position,
    ...floodFind(board, { rowIndex, colIndex: colIndex + 1 }, type),
    ...floodFind(board, { rowIndex, colIndex: colIndex - 1 }, type),
    ...floodFind(board, { colIndex, rowIndex: rowIndex + 1 }, type),
    ...floodFind(board, { colIndex, rowIndex: rowIndex - 1 }, type),
  ];
};
